import { ITEM_TITLE_KEYS } from '@components/CaseSidebar/config/items';
import { CASE_SIDEBAR_SECTIONS, SECTION_TITLE_KEYS } from './config/sections';
import { getAvailableOoPagesForStatus } from './resolve/ooStatusPages';
import { resolveItemIsDisabled, resolveItemSubTitle, resolveItemWarnings } from './resolve/itemFields';
import type { BuildBtlCaseSidebarParams, BuildBtlCaseSidebarResult, CaseSection, CaseSectionItem } from './types';
import { CaseSidebarItemId, CaseSidebarSection } from './types';
import type { BtlApplication, HouseholdsToHasIndividuals } from '@api/btlApplicationForm/types';
import { shouldBlockSubmissionForMissingFields } from '@common/missingFields';
import type { MissingCaseFields } from '@pages/BtlApplicationFormPage/types';
import type { TypedFormatMessageFn } from '@app-types/intl';

export interface OoCaseSidebarContext {
  status: string;
  canAccessHppPages: boolean;
  isAffordabilityPassed: boolean;
  isCreateMode?: boolean;
}

export interface BuildOoCaseSidebarParams {
  formatMessage: TypedFormatMessageFn;
  context: OoCaseSidebarContext;
  application?: BtlApplication;
  missingFields?: MissingCaseFields;
  isMissingFieldsComplete?: boolean;
  householdsToHasIndividuals?: HouseholdsToHasIndividuals;
  isAffordabilityPassed?: boolean;
  isAffordabilityRunRequired?: boolean;
}

function buildOoItemWarningsContext(params: BuildOoCaseSidebarParams): BuildBtlCaseSidebarParams | undefined {
  if (!params.application) {
    return undefined;
  }

  return {
    application: params.application,
    formatMessage: params.formatMessage,
    missingFields: params.missingFields,
    isMissingFieldsComplete: params.isMissingFieldsComplete,
    householdsToHasIndividuals: params.householdsToHasIndividuals,
    isAffordabilityPassed: params.isAffordabilityPassed,
    isAffordabilityRunRequired: params.isAffordabilityRunRequired,
    isAffordabilityRequired: params.context.canAccessHppPages,
    components: {},
  };
}

function buildSectionItem(
  itemId: CaseSidebarItemId,
  params: BuildOoCaseSidebarParams,
  warningsContext?: BuildBtlCaseSidebarParams,
): CaseSectionItem {
  const { formatMessage, application, missingFields, isMissingFieldsComplete } = params;
  const isSubmissionItem = itemId === CaseSidebarItemId.SUBMIT_DIP || itemId === CaseSidebarItemId.SUBMIT_APPLICATION;
  const isBlockedByMissingFields = shouldBlockSubmissionForMissingFields(
    application,
    missingFields,
    isMissingFieldsComplete,
  );

  return {
    id: itemId,
    title: formatMessage({ id: resolveOoSectionTitleKey(itemId) }),
    subTitle: application ? resolveItemSubTitle(itemId, application) : '',
    isDisabled: warningsContext
      ? resolveItemIsDisabled(itemId, warningsContext)
      : isSubmissionItem && isBlockedByMissingFields,
    component: null,
    warnings: warningsContext ? resolveItemWarnings(itemId, warningsContext) : [],
  };
}

const OO_ALWAYS_VISIBLE_APPLICATION_ITEM_IDS = [
  CaseSidebarItemId.FINANCING,
  CaseSidebarItemId.APPLICANTS,
  CaseSidebarItemId.EXPENSES,
];

const OO_HPP_APPLICATION_ITEM_IDS = [CaseSidebarItemId.PRODUCT, CaseSidebarItemId.AFFORDABILITY_CHECK];

const OO_SUBMISSION_APPLICATION_ITEM_IDS = [CaseSidebarItemId.SUBMIT_DIP, CaseSidebarItemId.SUBMIT_APPLICATION];

function getOoAlwaysVisibleApplicationItemIds(context: OoCaseSidebarContext): CaseSidebarItemId[] {
  return [...OO_ALWAYS_VISIBLE_APPLICATION_ITEM_IDS, ...(context.canAccessHppPages ? OO_HPP_APPLICATION_ITEM_IDS : [])];
}

const OO_APPLICATION_ITEM_ORDER: CaseSidebarItemId[] = [
  CaseSidebarItemId.FINANCING,
  CaseSidebarItemId.APPLICANTS,
  CaseSidebarItemId.EXPENSES,
  CaseSidebarItemId.PROPERTY,
  CaseSidebarItemId.PRODUCT,
  CaseSidebarItemId.AFFORDABILITY_CHECK,
  CaseSidebarItemId.SOLICITORS,
  CaseSidebarItemId.BANK_DETAILS,
  CaseSidebarItemId.SUBMIT_DIP,
  CaseSidebarItemId.SUBMIT_APPLICATION,
  CaseSidebarItemId.VALUATION,
  CaseSidebarItemId.POST_OFFER,
];

function sortOoApplicationItemIds(itemIds: CaseSidebarItemId[]): CaseSidebarItemId[] {
  const orderedItems = OO_APPLICATION_ITEM_ORDER.filter(itemId => itemIds.includes(itemId));
  const unorderedItems = itemIds.filter(itemId => !OO_APPLICATION_ITEM_ORDER.includes(itemId));

  return [...orderedItems, ...unorderedItems];
}

function filterItemsByHppAccess(itemIds: CaseSidebarItemId[], context: OoCaseSidebarContext): CaseSidebarItemId[] {
  if (context.canAccessHppPages) {
    return itemIds;
  }

  return itemIds.filter(itemId => !OO_HPP_APPLICATION_ITEM_IDS.includes(itemId));
}

function filterItemsBySubmissionAccess(
  itemIds: CaseSidebarItemId[],
  context: OoCaseSidebarContext,
): CaseSidebarItemId[] {
  // When HPP affordability is in the flow, hide submit tabs until affordability has passed.
  // Status chain still decides which submit tab exists: NEW → Submit DIP, DIP_ACCEPTED → Submit Application.
  if (context.canAccessHppPages && !context.isAffordabilityPassed) {
    return itemIds.filter(itemId => !OO_SUBMISSION_APPLICATION_ITEM_IDS.includes(itemId));
  }

  return itemIds;
}

function filterOoItemsByAccess(itemIds: CaseSidebarItemId[], context: OoCaseSidebarContext): CaseSidebarItemId[] {
  return filterItemsBySubmissionAccess(filterItemsByHppAccess(itemIds, context), context);
}

export function getVisibleOoItemIdsForSection(
  section: CaseSidebarSection,
  context: OoCaseSidebarContext,
): CaseSidebarItemId[] {
  if (context.isCreateMode) {
    return section === CaseSidebarSection.APPLICATION ? [CaseSidebarItemId.FINANCING] : [];
  }

  const pagesBySection = getAvailableOoPagesForStatus(context.status);

  if (section === CaseSidebarSection.APPLICATION) {
    const alwaysVisibleItemIds = getOoAlwaysVisibleApplicationItemIds(context);

    return sortOoApplicationItemIds(
      filterOoItemsByAccess(
        [
          ...alwaysVisibleItemIds,
          ...pagesBySection[CaseSidebarSection.APPLICATION].filter(itemId => !alwaysVisibleItemIds.includes(itemId)),
        ],
        context,
      ),
    );
  }

  return filterOoItemsByAccess(pagesBySection[section], context);
}

export function shouldShowOoOverviewNavItem(context: OoCaseSidebarContext): boolean {
  return !context.isCreateMode;
}

function buildOoOverviewNavItem(
  params: BuildOoCaseSidebarParams,
  warningsContext?: BuildBtlCaseSidebarParams,
): CaseSectionItem {
  return buildSectionItem(CaseSidebarItemId.OVERVIEW, params, warningsContext);
}

export function resolveOoSectionTitleKey(itemId: CaseSidebarItemId): (typeof ITEM_TITLE_KEYS)[CaseSidebarItemId] {
  if (itemId === CaseSidebarItemId.PRODUCT) {
    return 'ooApplicationForm.PRODUCT.label';
  }

  if (itemId === CaseSidebarItemId.AFFORDABILITY_CHECK) {
    return 'ooApplicationForm.AFFORDABILITY_CHECK.label';
  }

  return ITEM_TITLE_KEYS[itemId];
}

function buildApplicationSectionItems(
  params: BuildOoCaseSidebarParams,
  warningsContext?: BuildBtlCaseSidebarParams,
): CaseSectionItem[] {
  return getVisibleOoItemIdsForSection(CaseSidebarSection.APPLICATION, params.context).map(itemId =>
    buildSectionItem(itemId, params, warningsContext),
  );
}

function buildSectionsFromPages(
  pagesBySection: ReturnType<typeof getAvailableOoPagesForStatus>,
  params: BuildOoCaseSidebarParams,
  warningsContext?: BuildBtlCaseSidebarParams,
): CaseSection[] {
  const { formatMessage, context } = params;
  const sectionConfigs = [
    { section: CaseSidebarSection.DOCUMENTS, titleKey: SECTION_TITLE_KEYS[CaseSidebarSection.DOCUMENTS] },
    { section: CaseSidebarSection.APPLICATION, titleKey: SECTION_TITLE_KEYS[CaseSidebarSection.APPLICATION] },
    { section: CaseSidebarSection.OTHER, titleKey: SECTION_TITLE_KEYS[CaseSidebarSection.OTHER] },
  ];

  return sectionConfigs.reduce<CaseSection[]>((result, { section, titleKey }) => {
    const children =
      section === CaseSidebarSection.APPLICATION
        ? buildApplicationSectionItems(params, warningsContext)
        : getVisibleOoItemIdsForSection(section, context).map(itemId =>
            buildSectionItem(itemId, params, warningsContext),
          );

    if (children.length === 0) {
      return result;
    }

    result.push({
      title: formatMessage({ id: titleKey }),
      children,
    });

    return result;
  }, []);
}

export function getVisibleOoCaseSidebarItemIds(context: OoCaseSidebarContext): CaseSidebarItemId[] {
  if (context.isCreateMode) {
    return [CaseSidebarItemId.FINANCING];
  }

  const sectionItemIds = CASE_SIDEBAR_SECTIONS.flatMap(section => getVisibleOoItemIdsForSection(section, context));

  return [CaseSidebarItemId.OVERVIEW, ...sectionItemIds];
}

export function buildOoCaseSidebar(params: BuildOoCaseSidebarParams): BuildBtlCaseSidebarResult {
  const pagesBySection = getAvailableOoPagesForStatus(params.context.status);
  const warningsContext = buildOoItemWarningsContext(params);

  return {
    overviewItem: buildOoOverviewNavItem(params, warningsContext),
    state: {
      sections: buildSectionsFromPages(pagesBySection, params, warningsContext),
      isTopSlicing: false,
    },
  };
}
